import { z } from 'zod';
import type { TemplateDetail, TemplateField } from '../../overview/models/template.types';
import {
  controlKindOf,
  emptyValueFor,
  isSubmittableType,
  NUMBER_MAX_DIGITS,
  numberDigitCount,
  numberDigitsMessage,
  optionsOf,
  renderableSections,
} from '../models/templateRuntime';
import type { TemplateFormValues } from '../models/templateRuntime';

/**
 * A runtime form's Zod schema, derived from the template it renders.
 *
 * Three things can make a value invalid: the template's own Mandatory flag, the
 * shape the field's TYPE implies -- an email looks like an email, a date is a
 * date -- and, for a choice field with options configured, membership in that
 * list. A field stores no length or range, so none is checked here -- beyond a
 * Number's fixed twelve digits, which every Number holds (`NUMBER_MAX_DIGITS`); inventing
 * one would refuse input the template allows. The server applies the same
 * rules, so the client can never pass what the server refuses.
 *
 * Nested one object per section, because a field key is unique only within its
 * section -- RFQ and PR templates reuse `description` and `category` across
 * steps. The resulting error path, `sectionKey.fieldKey`, is the key the server
 * uses in `ApiError.errors[].field`.
 */

/** The server's own bound on a text value (`value_text`). */
export const TEMPLATE_TEXT_MAX = 4000;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^[0-9+\-() ]*$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

const required = (label: string) => `${label} is required`;

/** The builder's "Text -- Up to 100 characters" and "Long text -- Up to 500 characters", where a form applies them. */
export const TEMPLATE_TYPED_TEXT_MAX: Readonly<Record<string, number>> = {
  Text: 100,
  /* The PR Title (2026-10-14): "PR Title must be 100 characters or fewer". */
  'Short text': 100,
  'Long text': 500,
};

/** A string rule: required when mandatory, bounded, and one optional format check. */
function stringRule(
  field: TemplateField,
  format?: { test: (value: string) => boolean; message: string },
  max: number = TEMPLATE_TEXT_MAX,
): z.ZodTypeAny {
  let rule = z.string().trim().max(max, `${field.label} must be ${String(max)} characters or fewer`);
  if (field.mandatory) rule = rule.min(1, required(field.label));
  if (!format) return rule;
  return rule.refine((value) => value === '' || format.test(value), format.message);
}

/** The rule one submittable field is held to. */
export function ruleForField(field: TemplateField): z.ZodTypeAny {
  switch (controlKindOf(field.fieldType)) {
    case 'email':
      return stringRule(field, {
        test: (value) => EMAIL.test(value),
        message: 'Enter a valid email address',
      });
    case 'phone':
      return stringRule(field, {
        test: (value) => PHONE.test(value),
        message: 'Enter a valid phone number',
      });
    case 'url':
      return stringRule(field, {
        test: isHttpUrl,
        message: 'Enter a valid URL starting with http:// or https://',
      });
    case 'date':
      return stringRule(field, {
        test: (value) => ISO_DATE.test(value) && !Number.isNaN(Date.parse(value)),
        message: 'Enter a valid date',
      });
    case 'number':
    case 'currency': {
      const base = z.number({ invalid_type_error: `${field.label} must be a number` }).nullable();
      /* A Number holds up to twelve digits (the builder's "Up to 12 digits"); the server refuses more. */
      const rule =
        controlKindOf(field.fieldType) === 'number'
          ? base.refine(
              (value) => value === null || numberDigitCount(value) <= NUMBER_MAX_DIGITS,
              numberDigitsMessage(field.label),
            )
          : base;
      return field.mandatory
        ? rule.refine((value) => value !== null, required(field.label))
        : rule;
    }
    case 'checkbox':
      return field.mandatory
        ? z.boolean().refine((value) => value, required(field.label))
        : z.boolean();
    case 'multi': {
      const options = optionsOf(field);
      let rule = z.array(z.string());
      if (field.mandatory) rule = rule.min(1, required(field.label));
      if (options.length === 0) return rule;
      return rule.refine(
        (values) => values.every((value) => options.includes(value)),
        `Choose only from the options listed for ${field.label}`,
      );
    }
    case 'upload': {
      /* The file as a data URI -- not the text bound: a file is far longer than 4000 characters. */
      const rule = z
        .string()
        .refine((value) => value === '' || value.startsWith('data:'), `${field.label} must be an uploaded file`);
      return field.mandatory ? rule.refine((value) => value !== '', required(field.label)) : rule;
    }
    case 'choice': {
      const options = optionsOf(field);
      if (options.length === 0) return stringRule(field);
      return stringRule(field, {
        test: (value) => options.includes(value),
        message: `Choose one of the options for ${field.label}`,
      });
    }
    default:
      return stringRule(field);
  }
}

/**
 * The rule, with a Text held to 100 characters and a Long text to 500 when `typedLengths` -- the limits the builder's
 * type list names. A Purchase Request's form applies them (2026-10-14); the server holds it to the same.
 */
export function ruleForTypedField(field: TemplateField, typedLengths: boolean): z.ZodTypeAny {
  const max = typedLengths ? TEMPLATE_TYPED_TEXT_MAX[field.fieldType] : undefined;
  return max === undefined ? ruleForField(field) : stringRule(field, undefined, max);
}

/** Builds the schema for every submittable field the template renders. */
export function buildTemplateFormSchema(detail: Pick<TemplateDetail, 'sections'> & { kind?: TemplateDetail['kind'] }) {
  const sections: Record<string, z.ZodTypeAny> = {};
  /*
    The builder's typed limits apply on a Purchase Request's form, and on Supplier Onboarding's (2026-10-14): a supplier
    form is held to the 100 / 500 characters the Supplier template's builder shows beside its Text and Long text.
  */
  const typedLengths = detail.kind === 'PURCHASE_REQUEST' || detail.kind === 'SUPPLIER_ONBOARDING';

  for (const section of renderableSections(detail)) {
    const shape: Record<string, z.ZodTypeAny> = {};
    for (const field of section.fields) {
      if (isSubmittableType(field.fieldType)) shape[field.fieldKey] = ruleForTypedField(field, typedLengths);
    }
    sections[section.sectionKey] = z.object(shape);
  }

  return z.object(sections);
}

/** Blank values for every submittable field, so no control is ever uncontrolled. */
export function emptyTemplateFormValues(detail: Pick<TemplateDetail, 'sections'>): TemplateFormValues {
  const values: TemplateFormValues = {};

  for (const section of renderableSections(detail)) {
    const sectionValues: TemplateFormValues[string] = {};
    for (const field of section.fields) {
      if (isSubmittableType(field.fieldType)) {
        sectionValues[field.fieldKey] = emptyValueFor(field.fieldType);
      }
    }
    values[section.sectionKey] = sectionValues;
  }

  return values;
}
